'use client';

import type { GarminStrengthSet } from '@/utils/types';
import { useMemo } from 'react';

type Props = {
  strengthSets: GarminStrengthSet[];
};

const formatValue = (value: number | null | undefined, suffix = '') =>
  typeof value === 'number' && Number.isFinite(value)
    ? `${value}${suffix}`
    : 'n/a';

const formatText = (value: string | null | undefined, fallback = 'n/a') =>
  typeof value === 'string' && value.length > 0 ? value : fallback;

const formatDurationSeconds = (value: number | null | undefined) =>
  typeof value === 'number' && Number.isFinite(value)
    ? `${Math.round(value)}`
    : 'n/a';

export default function ActivityStrengthSets({ strengthSets }: Props) {
  const processedSets = useMemo(
    () =>
      strengthSets.map((set) => ({
        ...set,
        durationLength: set.durationValue ?? 0,
      })),
    [strengthSets]
  );

  if (!strengthSets || strengthSets.length === 0) {
    return (
      <div className='flex h-full items-center justify-center rounded-lg px-4 py-8 text-sm text-gray-500 dark:text-gray-400 bg:transparent border border-dashed border-gray-300 dark:border-gray-700'>
        No strength set data available.
      </div>
    );
  }

  return (
    <div className='flex h-full flex-col overflow-hidden'>
      <div className='grid grid-cols-[44px_minmax(120px,1.4fr)_minmax(96px,1fr)_48px_minmax(72px,auto)_64px_minmax(0,4fr)] items-center gap-2 px-3 pb-1 text-xs font-semibold text-gray-500 dark:text-gray-400'>
        <div>Set</div>
        <div>Exercise</div>
        <div>Category</div>
        <div>Reps</div>
        <div>Weight</div>
        <div>Duration</div>
        <div className='pl-2'>Duration</div>
      </div>
      <div className='flex-1 overflow-y-auto'>
        <div>
          {processedSets.map((set) => {
            const isRest = set.setType === 'rest' || set.isRest;
            const barWidth = Math.max(
              8,
              Math.min((set.durationValue ?? 0) * 2.4, 100)
            );
            return (
              <div
                className='grid grid-cols-[44px_minmax(120px,1.4fr)_minmax(96px,1fr)_48px_minmax(72px,auto)_64px_minmax(0,4fr)] items-center gap-1 px-3 py-0.5 text-sm leading-none'
                key={`${set.activityId}-${set.setIndex}`}
              >
                <div className='font-mono text-gray-600 dark:text-gray-300'>
                  {set.setIndex + 1}
                </div>
                <div className='truncate font-medium text-gray-900 dark:text-gray-100'>
                  {formatText(isRest ? 'rest' : set.exerciseName)}
                </div>
                <div className='truncate text-gray-600 dark:text-gray-300'>
                  {formatText(isRest ? 'rest' : set.exerciseCategory)}
                </div>
                <div className='text-gray-600 dark:text-gray-300'>
                  {isRest ? 'rest' : formatValue(set.repetitionValue)}
                </div>
                <div className='truncate text-gray-600 dark:text-gray-300'>
                  {isRest
                    ? 'rest'
                    : formatValue(
                        set.weightValue,
                        set.weightDisplayUnit ? ` ${set.weightDisplayUnit}` : ''
                      )}
                </div>
                <div className='text-gray-600 dark:text-gray-300'>
                  {formatDurationSeconds(set.durationValue)}
                </div>
                <div className='min-w-0 pl-1'>
                  <div className='flex h-5 items-center'>
                    <div
                      className={`h-5 ${isRest ? 'bg-gray-300 dark:bg-gray-700' : 'bg-teal-700 dark:bg-teal-600'}`}
                      style={{ width: `${barWidth}%` }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
